import { formatPercentage } from '@/domain/common/format'
import { Percentage } from '@/domain/types/NumericValues'
import { Button } from '@/ui/atoms/button/Button'
import { Checkbox } from '@/ui/atoms/checkbox/Checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/ui/atoms/dialog/Dialog'
import { Typography } from '@/ui/atoms/typography/Typography'
import { SlippageWarning } from '@/ui/molecules/slippage-panel/SlippagePanel'
import { cn } from '@/ui/utils/style'
import { AlertTriangle, ExternalLink } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { GasLimitExceededDetails } from '../logic/constants'
import { BigBlocksSwapFlow } from './BigBlocksSwapFlow'

interface GasLimitExceededModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  gasLimitDetails: GasLimitExceededDetails | null
  slippage: Percentage
  setSlippage: (slippage: Percentage) => void
  isUsingBigBlocks: boolean
  isHypercoreUser: boolean
  /** Execute the swap - should return true if successful */
  executeSwap: () => Promise<boolean>
  /** Current swap state message */
  swapState: string
  /** Whether swap encountered an error */
  swapError: boolean
  /** Called when swap completes successfully */
  onSwapSuccess: () => void
}

const SLIPPAGE_OPTIONS = [Percentage(0.001), Percentage(0.005), Percentage(0.01)]

function formatGas(gas: bigint): string {
  const millions = Number(gas) / 1_000_000
  return `${millions.toFixed(1)}M`
}

export function GasLimitExceededModal({
  open,
  onOpenChange,
  gasLimitDetails,
  slippage,
  setSlippage,
  isUsingBigBlocks,
  isHypercoreUser,
  executeSwap,
  swapState,
  swapError,
  onSwapSuccess,
}: GasLimitExceededModalProps) {
  const [selectedSlippage, setSelectedSlippage] = useState<Percentage>(slippage)
  const [customSlippage, setCustomSlippage] = useState('')
  const [isCustomInput, setIsCustomInput] = useState(false)
  const [switchBackAfter, setSwitchBackAfter] = useState(true)
  const [showBigBlocksFlow, setShowBigBlocksFlow] = useState(false)

  useEffect(() => {
    if (open) {
      setSelectedSlippage(slippage)
      setCustomSlippage('')
      setIsCustomInput(false)
      setShowBigBlocksFlow(false)
    }
  }, [open, slippage])

  const handleSlippageSelect = (option: Percentage) => {
    setSelectedSlippage(option)
    setIsCustomInput(false)
    setCustomSlippage('')
  }

  const handleCustomSlippageChange = (value: string) => {
    setCustomSlippage(value)
    const numValue = Number.parseFloat(value)
    if (!Number.isNaN(numValue) && numValue > 0 && numValue < 50) {
      setSelectedSlippage(Percentage(numValue / 100))
    }
  }

  const handleUpdateSlippage = () => {
    setSlippage(selectedSlippage)
    onOpenChange(false)
  }

  const handleStartBigBlocksFlow = () => {
    setShowBigBlocksFlow(true)
  }

  const handleBigBlocksFlowComplete = () => {
    onSwapSuccess()
    onOpenChange(false)
  }

  const handleBigBlocksFlowCancel = () => {
    setShowBigBlocksFlow(false)
  }

  const isCustomSlippageActive = !SLIPPAGE_OPTIONS.some((option) => selectedSlippage.eq(option))

  // If showing the big blocks flow, render only that
  if (showBigBlocksFlow) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[480px]" showCloseButton={false}>
          <BigBlocksSwapFlow
            switchBackAfter={switchBackAfter}
            executeSwap={executeSwap}
            swapState={swapState}
            swapError={swapError}
            onComplete={handleBigBlocksFlowComplete}
            onCancel={handleBigBlocksFlowCancel}
          />
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            <DialogTitle>Transaction Requires More Gas</DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <Typography variant="p" className="text-secondary-foreground text-sm">
            This swap requires ~{gasLimitDetails ? formatGas(gasLimitDetails.estimatedGas) : '?'} gas, which exceeds the
            small block limit ({gasLimitDetails ? formatGas(gasLimitDetails.limit) : '3M'}).
          </Typography>

          {/* Option 1: Higher Slippage */}
          <div className="rounded-lg border border-border bg-background/50 p-4">
            <Typography variant="h4" className="mb-2 text-base">
              Option 1: Try Higher Slippage
            </Typography>
            <Typography variant="p" className="mb-4 text-secondary-foreground text-sm">
              Higher slippage often results in simpler routes that use less gas.
            </Typography>

            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {SLIPPAGE_OPTIONS.map((option) => (
                  <button
                    key={option.toString()}
                    onClick={() => handleSlippageSelect(option)}
                    className={cn(
                      'rounded border border-border px-3 py-1.5 text-sm transition-colors hover:bg-accent hover:text-foreground',
                      selectedSlippage.eq(option) && 'border-primary bg-primary/10 text-primary',
                    )}
                  >
                    {formatPercentage(option, { minimumFractionDigits: 0 })}
                  </button>
                ))}
                <button
                  onClick={() => setIsCustomInput(true)}
                  className={cn(
                    'rounded border border-border px-3 py-1.5 text-sm transition-colors hover:bg-accent hover:text-foreground',
                    isCustomSlippageActive && 'border-primary bg-primary/10 text-primary',
                  )}
                >
                  Custom
                </button>
              </div>

              {isCustomInput && (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    value={customSlippage}
                    onChange={(e) => handleCustomSlippageChange(e.target.value)}
                    placeholder="e.g. 3"
                    min="0.1"
                    max="49.9"
                    step="0.1"
                    className="w-24 rounded border border-border bg-background px-2 py-1 text-sm [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none focus:border-primary focus:outline-none"
                  />
                  <span className="text-muted-foreground text-sm">%</span>
                  <SlippageWarning slippage={selectedSlippage} />
                </div>
              )}

              <Button onClick={handleUpdateSlippage} className="w-full" size="sm">
                Update Slippage to {formatPercentage(selectedSlippage, { minimumFractionDigits: 0 })}{' '}
                <SlippageWarning slippage={selectedSlippage} />
              </Button>
            </div>
          </div>

          {/* Option 2: Big Blocks */}
          <div className="rounded-lg border border-border bg-background/50 p-4">
            <Typography variant="h4" className="mb-2 text-base">
              Option 2: Enable Big Blocks
            </Typography>

            {isUsingBigBlocks ? (
              <Typography variant="p" className="text-green-500 text-sm">
                You are already using big blocks. Try the swap again - it should work.
              </Typography>
            ) : isHypercoreUser ? (
              <>
                <Typography variant="p" className="mb-4 text-secondary-foreground text-sm">
                  Big blocks have a 30M gas limit but take ~1 minute to confirm.
                </Typography>

                <div className="mb-4 flex items-center gap-2">
                  <Checkbox
                    id="switch-back"
                    checked={switchBackAfter}
                    onCheckedChange={(checked) => setSwitchBackAfter(checked === true)}
                  />
                  <label htmlFor="switch-back" className="cursor-pointer text-sm">
                    Switch back to small blocks after swap
                  </label>
                </div>

                <Button onClick={handleStartBigBlocksFlow} className="w-full" size="sm">
                  Enable Big Blocks & Swap
                </Button>
              </>
            ) : (
              <div className="space-y-3">
                <div className="flex items-start gap-2 rounded border border-yellow-500/30 bg-yellow-500/10 p-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-yellow-500" />
                  <div>
                    <Typography variant="p" className="font-medium text-sm">
                      Requires Hypercore Account
                    </Typography>
                    <Typography variant="p" className="mt-1 text-secondary-foreground text-xs">
                      To use big blocks, you need to activate your Hypercore account by depositing at least $1 of USDC
                      or HYPE on L1.
                    </Typography>
                  </div>
                </div>

                <Button variant="secondary" className="w-full" size="sm" asChild>
                  <a href="https://app.hyperliquid.xyz" target="_blank" rel="noopener noreferrer">
                    Open Hyperliquid
                    <ExternalLink className="ml-2 h-3 w-3" />
                  </a>
                </Button>
              </div>
            )}
          </div>

          <Button variant="ghost" onClick={() => onOpenChange(false)} className="w-full">
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
